import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  NativeSelectRoot,
  NativeSelectField,
} from '@chakra-ui/react';
import { AccountRecord, CreateAccountInput } from '@/types/account';
import { ROLE_LABELS, UserRole } from '@/types/user';

interface AccountFormValues {
  email: string;
  userName: string;
  role: UserRole;
}

interface AccountFormProps {
  initialData?: AccountRecord;
  isSubmitting: boolean;
  onSubmit: (data: CreateAccountInput) => void | Promise<void>;
  onCancel: () => void;
  /** 自分自身のアカウント編集時にメール変更の注意を表示する */
  showSelfEmailWarning?: boolean;
  /** 自分自身のアカウント編集時に権限欄を無効化する（自己降格による締め出しの防止） */
  disableRoleChange?: boolean;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * アカウント作成・編集フォームコンポーネント
 * パスワード入力欄は持たない（作成時にサーバーが16文字を自動発行するため）
 */
export const AccountForm: FC<AccountFormProps> = ({
  initialData,
  isSubmitting,
  onSubmit,
  onCancel,
  showSelfEmailWarning = false,
  disableRoleChange = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<AccountFormValues>({ defaultValues: { role: 'user' } });

  const isEditMode = !!initialData;

  // 編集時のプレフィル
  useEffect(() => {
    if (initialData) {
      setValue('email', initialData.email);
      setValue('userName', initialData.userName);
      setValue('role', initialData.role);
    }
  }, [initialData, setValue]);

  const handleFormSubmit = (values: AccountFormValues) => {
    return onSubmit({
      email: values.email,
      userName: values.userName,
      // 権限欄が無効なときは元の値を送る（無効な select の値に依存しない）
      role: disableRoleChange && initialData ? initialData.role : values.role,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <VStack gap={6} align="stretch">
        {/* メールアドレス */}
        <Box>
          <Text asChild fontSize="sm" fontWeight="medium" mb={2} display="block">
            <label htmlFor="account-email">
              メールアドレス <Text as="span" color="red.500">*</Text>
            </label>
          </Text>
          <Input
            id="account-email"
            type="text"
            {...register('email', {
              required: 'メールアドレスは必須です',
              pattern: { value: EMAIL_PATTERN, message: 'メールアドレスの形式が不正です' },
            })}
          />
          {errors.email && (
            <Text fontSize="sm" color="red.500" mt={1}>
              {errors.email.message}
            </Text>
          )}
          {showSelfEmailWarning && (
            <Text fontSize="xs" color="orange.600" mt={1}>
              ※ 自分自身のメールアドレスを変更した場合、変更後は新しいメールアドレスで再ログインが必要です
            </Text>
          )}
        </Box>

        {/* ユーザー名 */}
        <Box>
          <Text asChild fontSize="sm" fontWeight="medium" mb={2} display="block">
            <label htmlFor="account-user-name">
              ユーザー名 <Text as="span" color="red.500">*</Text>
            </label>
          </Text>
          <Input
            id="account-user-name"
            {...register('userName', {
              required: 'ユーザー名は必須です',
              maxLength: { value: 100, message: 'ユーザー名は100文字以内で入力してください' },
            })}
          />
          {errors.userName && (
            <Text fontSize="sm" color="red.500" mt={1}>
              {errors.userName.message}
            </Text>
          )}
        </Box>

        {/* 権限 */}
        <Box>
          <Text asChild fontSize="sm" fontWeight="medium" mb={2} display="block">
            <label htmlFor="account-role">
              権限 <Text as="span" color="red.500">*</Text>
            </label>
          </Text>
          <NativeSelectRoot>
            <NativeSelectField
              id="account-role"
              disabled={disableRoleChange}
              {...register('role', { required: '権限を選択してください' })}
            >
              <option value="user">{ROLE_LABELS.user}</option>
              <option value="admin">{ROLE_LABELS.admin}</option>
            </NativeSelectField>
          </NativeSelectRoot>
          {errors.role && (
            <Text fontSize="sm" color="red.500" mt={1}>
              {errors.role.message}
            </Text>
          )}
          {disableRoleChange ? (
            <Text fontSize="xs" color="orange.600" mt={1}>
              ※ 自分自身の権限は変更できません
            </Text>
          ) : (
            <Text fontSize="xs" color="gray.600" mt={1}>
              ※ 管理者はアカウント管理・事業者管理を操作できます
            </Text>
          )}
        </Box>

        {!isEditMode && (
          <Text fontSize="xs" color="gray.600">
            ※ パスワードは16文字（英大文字・小文字・数字・記号）が自動発行され、作成完了時に一度だけ表示されます
          </Text>
        )}

        {/* ボタン */}
        <HStack gap={3}>
          <Button variant="outline" onClick={onCancel} flex={1}>
            キャンセル
          </Button>
          <Button type="submit" colorPalette="blue" flex={1} loading={isSubmitting}>
            {isEditMode ? '保存' : '作成'}
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
